/**
 * Shopify Theme Custom Mega Menu & Mobile Drawer
 * Implements per-menu unique layouts, single-open state, click-outside/ESC close,
 * and accessible mobile navigation drawer with accordions.
 */

(function () {
  'use strict';

  class MegaMenuController {
    constructor(headerElement) {
      this.header = headerElement || document.querySelector('.site-header');
      if (!this.header) return;

      this.triggers = this.header.querySelectorAll('[data-mega-trigger]');
      this.menus = this.header.querySelectorAll('[data-mega-menu]');
      this.backdrop = this.header.querySelector('.mega-menu-backdrop');
      this.activeMenuId = null;

      // Mobile elements
      this.mobileToggle = document.getElementById('MobileMenuToggle');
      this.mobileDrawer = document.getElementById('MobileNavDrawer');
      this.mobileClose = document.getElementById('MobileDrawerClose');
      this.mobileBackdrop = document.getElementById('MobileDrawerBackdrop');
      this.mobileAccordionTriggers = document.querySelectorAll('[data-mobile-accordion-trigger]');

      this.init();
    }

    init() {
      this.bindDesktopEvents();
      this.bindMobileEvents();
      this.bindAccessibility();
      this.bindShopCarousels();
    }

    bindShopCarousels() {
      const carousels = this.header.querySelectorAll('[data-shop-carousel]');
      carousels.forEach((carousel) => {
        const slides = carousel.querySelectorAll('[data-shop-slide]');
        const prevBtn = carousel.querySelector('[data-shop-carousel-prev]');
        const nextBtn = carousel.querySelector('[data-shop-carousel-next]');
        const counter = carousel.querySelector('[data-shop-carousel-counter]');
        if (!slides.length) return;

        let current = 1;
        const total = slides.length;

        const updateSlide = (newIndex) => {
          current = newIndex;
          slides.forEach((slide) => {
            const idx = parseInt(slide.getAttribute('data-shop-slide'), 10);
            if (idx === current) {
              slide.classList.add('is-active');
            } else {
              slide.classList.remove('is-active');
            }
          });
          if (counter) {
            counter.textContent = `${current}/${total}`;
          }
        };

        if (nextBtn) {
          nextBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const nextIdx = current >= total ? 1 : current + 1;
            updateSlide(nextIdx);
          });
        }

        if (prevBtn) {
          prevBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const prevIdx = current <= 1 ? total : current - 1;
            updateSlide(prevIdx);
          });
        }
      });
    }

    bindDesktopEvents() {
      this.hoverTimeout = null;
      this.openTimeout = null;

      const clearTimer = () => {
        if (this.hoverTimeout) {
          clearTimeout(this.hoverTimeout);
          this.hoverTimeout = null;
        }
        if (this.openTimeout) {
          clearTimeout(this.openTimeout);
          this.openTimeout = null;
        }
      };

      const startCloseTimer = (delay = 220) => {
        if (this.openTimeout) {
          clearTimeout(this.openTimeout);
          this.openTimeout = null;
        }
        if (this.hoverTimeout) {
          clearTimeout(this.hoverTimeout);
        }
        this.hoverTimeout = setTimeout(() => {
          this.closeAllMegaMenus();
          this.hoverTimeout = null;
        }, delay);
      };

      // Header bounds: menu remains rock-solid open while cursor is inside header or any mega menu
      this.header.addEventListener('mouseenter', () => {
        if (window.innerWidth >= 1024) {
          clearTimer();
        }
      });

      this.header.addEventListener('mouseleave', () => {
        if (window.innerWidth >= 1024 && this.activeMenuId) {
          startCloseTimer(220);
        }
      });

      // Toggle / switch mega menu on trigger hover, focus, and click
      this.triggers.forEach((trigger) => {
        const targetId = trigger.getAttribute('data-mega-trigger');
        const parentItem = trigger.closest('.site-header__menu-item') || trigger.parentElement;

        if (parentItem) {
          parentItem.addEventListener('mouseenter', () => {
            if (window.innerWidth >= 1024) {
              clearTimer();
              // If already open, switch immediately without closing/reopening
              if (this.activeMenuId) {
                this.openMegaMenu(targetId);
                return;
              }
              // Gentle open delay (50ms) to prevent accidental flashing when cursor glances across
              this.openTimeout = setTimeout(() => {
                this.openMegaMenu(targetId);
                this.openTimeout = null;
              }, 50);
            }
          });
        }

        // Keyboard focus support
        trigger.addEventListener('focus', () => {
          if (window.innerWidth >= 1024) {
            clearTimer();
            this.openMegaMenu(targetId);
          }
        });

        // Click support (toggle)
        trigger.addEventListener('click', (e) => {
          const targetMenu = this.header.querySelector(`[data-mega-menu="${targetId}"]`);
          if (targetMenu) {
            e.preventDefault();
            clearTimer();
            if (this.activeMenuId === targetId) {
              this.closeAllMegaMenus();
            } else {
              this.openMegaMenu(targetId);
            }
          }
        });
      });

      // Close when hovering non-mega top-level links (e.g. Home, Contact)
      const topNavLinks = this.header.querySelectorAll('.site-header__menu-link:not([data-mega-trigger])');
      topNavLinks.forEach((link) => {
        link.addEventListener('mouseenter', () => {
          if (window.innerWidth >= 1024) {
            if (this.openTimeout) {
              clearTimeout(this.openTimeout);
              this.openTimeout = null;
            }
            if (this.activeMenuId) {
              startCloseTimer(150);
            }
          }
        });
        link.addEventListener('focus', () => {
          if (window.innerWidth >= 1024 && this.activeMenuId) {
            clearTimer();
            this.closeAllMegaMenus();
          }
        });
      });

      // Focusout check on header to close when focus moves outside
      this.header.addEventListener('focusout', (e) => {
        if (window.innerWidth >= 1024 && !this.header.contains(e.relatedTarget)) {
          clearTimer();
          this.closeAllMegaMenus();
        }
      });

      // Clicking outside closes all menus
      document.addEventListener('click', (e) => {
        if (!this.header.contains(e.target)) {
          clearTimer();
          this.closeAllMegaMenus();
        }
      });

      // Clicking on backdrop closes mega menu
      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => {
          clearTimer();
          this.closeAllMegaMenus();
        });
      }

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' || e.key === 'Esc') {
          clearTimer();
          if (this.activeMenuId) {
            const currentTrigger = this.header.querySelector(`[data-mega-trigger="${this.activeMenuId}"]`);
            this.closeAllMegaMenus();
            if (currentTrigger) currentTrigger.focus();
          }
          this.closeMobileDrawer();
        }
      });
    }

    openMegaMenu(menuId) {
      const targetMenu = this.header.querySelector(`[data-mega-menu="${menuId}"]`);
      if (!targetMenu) return;

      // Close previous
      this.triggers.forEach((t) => {
        t.setAttribute('aria-expanded', 'false');
        t.classList.remove('is-active');
        const parent = t.closest('.site-header__menu-item');
        if (parent) parent.classList.remove('is-active');
      });

      this.menus.forEach((m) => {
        if (m !== targetMenu) {
          m.classList.remove('is-active');
          m.setAttribute('aria-hidden', 'true');
        }
      });

      // Open new
      const activeTrigger = this.header.querySelector(`[data-mega-trigger="${menuId}"]`);
      if (activeTrigger) {
        activeTrigger.setAttribute('aria-expanded', 'true');
        activeTrigger.classList.add('is-active');
        const parent = activeTrigger.closest('.site-header__menu-item');
        if (parent) parent.classList.add('is-active');
      }

      targetMenu.classList.add('is-active');
      targetMenu.setAttribute('aria-hidden', 'false');
      this.activeMenuId = menuId;

      if (this.backdrop) {
        this.backdrop.classList.add('is-active');
      }
      this.header.classList.add('has-open-mega');
    }

    closeAllMegaMenus() {
      this.triggers.forEach((t) => {
        t.setAttribute('aria-expanded', 'false');
        t.classList.remove('is-active');
        const parent = t.closest('.site-header__menu-item');
        if (parent) parent.classList.remove('is-active');
      });

      this.menus.forEach((m) => {
        m.classList.remove('is-active');
        m.setAttribute('aria-hidden', 'true');
      });

      if (this.backdrop) {
        this.backdrop.classList.remove('is-active');
      }
      this.header.classList.remove('has-open-mega');
      this.activeMenuId = null;
    }

    bindMobileEvents() {
      if (this.mobileToggle && this.mobileDrawer) {
        this.mobileToggle.addEventListener('click', () => {
          this.openMobileDrawer();
        });
      }

      if (this.mobileClose) {
        this.mobileClose.addEventListener('click', () => {
          this.closeMobileDrawer();
        });
      }

      if (this.mobileBackdrop) {
        this.mobileBackdrop.addEventListener('click', () => {
          this.closeMobileDrawer();
        });
      }

      // Accordion logic for mobile items
      this.mobileAccordionTriggers.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const isExpanded = btn.getAttribute('aria-expanded') === 'true';
          const contentId = btn.getAttribute('data-mobile-accordion-trigger');
          const content = document.getElementById(contentId);

          if (!content) return;

          if (isExpanded) {
            btn.setAttribute('aria-expanded', 'false');
            content.style.maxHeight = null;
            content.classList.remove('is-expanded');
          } else {
            // Close siblings for clean accordion behavior
            const parentList = btn.closest('.mobile-nav__list');
            if (parentList) {
              parentList.querySelectorAll('[data-mobile-accordion-trigger]').forEach((otherBtn) => {
                if (otherBtn !== btn) {
                  otherBtn.setAttribute('aria-expanded', 'false');
                  const otherContentId = otherBtn.getAttribute('data-mobile-accordion-trigger');
                  const otherContent = document.getElementById(otherContentId);
                  if (otherContent) {
                    otherContent.style.maxHeight = null;
                    otherContent.classList.remove('is-expanded');
                  }
                }
              });
            }

            btn.setAttribute('aria-expanded', 'true');
            content.classList.add('is-expanded');
            content.style.maxHeight = content.scrollHeight + 'px';
          }
        });
      });
    }

    openMobileDrawer() {
      if (!this.mobileDrawer) return;
      this.mobileDrawer.classList.add('is-open');
      this.mobileDrawer.setAttribute('aria-hidden', 'false');
      if (this.mobileToggle) {
        this.mobileToggle.setAttribute('aria-expanded', 'true');
      }
      if (this.mobileBackdrop) {
        this.mobileBackdrop.classList.add('is-open');
      }
      document.body.classList.add('mobile-nav-lock');
    }

    closeMobileDrawer() {
      if (!this.mobileDrawer) return;
      this.mobileDrawer.classList.remove('is-open');
      this.mobileDrawer.setAttribute('aria-hidden', 'true');
      if (this.mobileToggle) {
        this.mobileToggle.setAttribute('aria-expanded', 'false');
      }
      if (this.mobileBackdrop) {
        this.mobileBackdrop.classList.remove('is-open');
      }
      document.body.classList.remove('mobile-nav-lock');
    }

    bindAccessibility() {
      // Enable keyboard arrow navigation between menu items
      this.triggers.forEach((trigger, index) => {
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' && index < this.triggers.length - 1) {
            this.triggers[index + 1].focus();
          } else if (e.key === 'ArrowLeft' && index > 0) {
            this.triggers[index - 1].focus();
          } else if (e.key === 'ArrowDown') {
            const menuId = trigger.getAttribute('data-mega-trigger');
            const menu = this.header.querySelector(`[data-mega-menu="${menuId}"]`);
            if (menu && menu.classList.contains('is-active')) {
              const firstLink = menu.querySelector('a, button');
              if (firstLink) {
                e.preventDefault();
                firstLink.focus();
              }
            }
          }
        });
      });
    }
  }

  // Initialize on DOM ready
  function initHeaderMegaMenu() {
    const header = document.querySelector('.site-header');
    if (header) {
      window.megaMenuInstance = new MegaMenuController(header);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeaderMegaMenu);
  } else {
    initHeaderMegaMenu();
  }

  // Shopify theme customizer reloads
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.classList.contains('header-section') || e.target.querySelector('.site-header')) {
      initHeaderMegaMenu();
    }
  });

  document.addEventListener('shopify:block:select', function (e) {
    const blockId = e.detail.blockId;
    const blockEl = document.querySelector(`[data-block-id="${blockId}"]`);
    if (blockEl && window.megaMenuInstance) {
      const menuId = blockEl.getAttribute('data-mega-menu');
      if (menuId) {
        window.megaMenuInstance.openMegaMenu(menuId);
      }
    }
  });
})();
